import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonButton, IonButtons, IonIcon, IonList } from '@ionic/react'
import { chevronForwardOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { getBodySurveySelector } from '../selectors'
import BodyParts from '../bodyParts/BodyParts.content'
import useDeviceMetrics from '../../../../../utilities/useDeviceMetrics'
import { setDocumentFindings, setSelectedBodyPart } from '../store/bodySurvey.actions'
import { getBodyAssessmentSelector } from '../../selectors/getBodyAssessment.selector'
import BodyExternalSkin from '../externalSkin/externalSkin.content'
import GeneralExtremities from '../generalExtremeties/generalExtremeties.content'
import './BodySurvey.content.css'
import { getDocumentedLocations } from '../../../../../utilities/bodySurvey.util'
import { getDatapointsConfigSelector } from '../../../../configurations/selectors'
import { IDataPointsConfig } from '../../../../configurations/store/configurations.types'
import { setBodySelectionMode } from '../../store/bodyAssessment.actions'

interface IButtonProps {
  onClick: () => void
  label: string
  className?: string
}

const DocumentFindingsButton: React.FC<IButtonProps> = ({ onClick, label, className = '' }) => (
  <IonButton slot='end' onClick={onClick} className={className} color='primary-alt'>
    {label}
    <IonIcon icon={chevronForwardOutline} />
  </IonButton>
)

const BodySurveyContent: React.FC = () => {
  const { isMobile, isTablet, isTabletInLandscape, orientation } = useDeviceMetrics()
  const bodySurveyState = useSelector(getBodySurveySelector)
  const { bodySelectionMode } = useSelector(getBodyAssessmentSelector)
  const dataPointsConfig: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_GENERAL_EXTREMITIES } = dataPointsConfig

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { __selectedBodyParts, __selectedBodyPart, examValues, examValuesPNNVs, findings } = bodySurveyState
  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocationIds = getDocumentedLocations(combinedExamOptions, findings).map((location) => location.id)

  const isGeneralExtremitiesDataPresent = allDocumentedLocationIds.includes(10)
  const hideGeneralExtremities =
    DP_GENERAL_EXTREMITIES === 0 && !isGeneralExtremitiesDataPresent && bodySelectionMode?.id === 3

  useEffect(() => {
    if (hideGeneralExtremities) {
      dispatch(setBodySelectionMode({ id: 1, name: 'Body parts' }))
    }
  }, [DP_GENERAL_EXTREMITIES])

  useEffect(() => {
    dispatch(setSelectedBodyPart(null))
  }, [orientation])

  const handleFindings = () => {
    dispatch(setDocumentFindings(true))
  }

  const renderBodyContent = () => {
    switch (bodySelectionMode.id) {
      case 1:
        return <BodyParts bodySurveyState={bodySurveyState} />
      case 2:
        return <BodyExternalSkin />
      case 3:
        return <GeneralExtremities />
      default:
        return null
    }
  }

  const showDocumentFindingsButton =
    (isMobile || (isTablet && !isTabletInLandscape) || (!isTablet && orientation !== 'landscape')) &&
    ((bodySelectionMode?.id === 1 && __selectedBodyParts.length > 0) ||
      bodySelectionMode?.id === 2 ||
      bodySelectionMode?.id === 3) &&
    !__selectedBodyPart?.hasZoom

  const StatusDots = () => (
    <div className='status-dots'>
      <span>
        <span className='status-base status-completed'></span>
        {t('additionalAssessments.bodySurvey.completed')}
      </span>
      <span>
        <span className='status-base status-in-progress'></span>
        {t('additionalAssessments.bodySurvey.currentSelection')}
      </span>
    </div>
  )

  return (
    <>
      <IonList id='BodySurveyContent' lines='none' className='body-survey-content'>
        <IonButtons className='justify-end'>
          {showDocumentFindingsButton && (
            <DocumentFindingsButton
              className='ml-auto'
              onClick={handleFindings}
              label={t('additionalAssessments.bodySurvey.documentFindings')}
            />
          )}
        </IonButtons>
        {renderBodyContent()}
      </IonList>
      {bodySelectionMode?.id === 1 && <StatusDots />}
    </>
  )
}

export default BodySurveyContent
